import * as React from 'react';
import usePatchElement from '../../_util/hooks/usePatchElement';
import { withConfirm, withError, withInfo, withSuccess, withWarn } from '../confirm';
import destroyFns from '../destroyFns';
import HookModal from './HookModal';
let uuid = 0;
const ElementsHolder = React.memo(React.forwardRef((_props, ref) => {
    const [elements, patchElement] = usePatchElement();
    React.useImperativeHandle(ref, () => ({ patchElement }), [patchElement]);
    return React.createElement(React.Fragment, null, elements);
}));
function useModal() {
    const holderRef = React.useRef(null);
    // ========================== Effect ==========================
    const [actionQueue, setActionQueue] = React.useState([]);
    React.useEffect(() => {
        if (actionQueue.length) {
            const cloneQueue = [...actionQueue];
            cloneQueue.forEach((action) => {
                action();
            });
            setActionQueue([]);
        }
    }, [actionQueue]);
    // =========================== Hook ===========================
    const getConfirmFunc = React.useCallback((withFunc) => function hookConfirm(config) {
        var _a;
        uuid += 1;
        const modalRef = React.createRef();
        // Proxy to promise with `onClose`
        let resolvePromise;
        const promise = new Promise((resolve) => {
            resolvePromise = resolve;
        });
        let silent = false;
        let closeFunc;
        const modal = (React.createElement(HookModal, { key: `modal-${uuid}`, config: withFunc(config), ref: modalRef, afterClose: () => {
                closeFunc === null || closeFunc === void 0 ? void 0 : closeFunc();
            }, isSilent: () => silent, onConfirm: (confirmed) => {
                resolvePromise(confirmed);
            } }));
        closeFunc = (_a = holderRef.current) === null || _a === void 0 ? void 0 : _a.patchElement(modal);
        if (closeFunc) {
            destroyFns.push(closeFunc);
        }
        const instance = {
            destroy: () => {
                function destroyAction() {
                    var _a;
                    (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
                }
                if (modalRef.current) {
                    destroyAction();
                }
                else {
                    setActionQueue((prev) => [...prev, destroyAction]);
                }
            },
            update: (newConfig) => {
                function updateAction() {
                    var _a;
                    (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.update(newConfig);
                }
                if (modalRef.current) {
                    updateAction();
                }
                else {
                    setActionQueue((prev) => [...prev, updateAction]);
                }
            },
            then: (resolve) => {
                silent = true;
                return promise.then(resolve);
            },
        };
        return instance;
    }, []);
    const fns = React.useMemo(() => ({
        info: getConfirmFunc(withInfo),
        success: getConfirmFunc(withSuccess),
        error: getConfirmFunc(withError),
        warning: getConfirmFunc(withWarn),
        confirm: getConfirmFunc(withConfirm),
    }), [getConfirmFunc]);
    return [fns, React.createElement(ElementsHolder, { key: "modal-holder", ref: holderRef })];
}
export default useModal;
